import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { AccessRequestStatus, AccessRequestView } from '../lib/admin-access-requests';
import { absolute } from '../lib/time';
import { DataTable, type DataTableColumn } from './DataTable';
import { StatusPill, type StatusTone } from './StatusPill';
import { Button } from './ui/Button';

// The access-request inbox as a dense data table (spec §3.15, arc 101 slice 01).
// PRESENTER: the panel owns the accumulated rows, the filter, the writes, the
// busy map, the invite dialog and pagination. Invite / Decline are offered on a
// PENDING row only — a decided application shows when it was decided instead,
// since both verbs are terminal server-side and a button there could only spend
// a round trip on a 409. Decline only ASKS (`onDecline` opens the panel's
// confirm, plan 101 slice 17 row 17-16): terminal, and one click away beside Invite.

/** Status → pill tone, and the label key. Literal keys so `i18n.coverage` sees each. */
const STATUS: Readonly<Record<AccessRequestStatus, { key: string; tone: StatusTone }>> = {
  pending: { key: 'admin.access_status_pending', tone: 'warning' },
  invited: { key: 'admin.access_status_invited', tone: 'success' },
  declined: { key: 'admin.access_status_declined', tone: 'neutral' },
};

/** A re-submission bumps `updated_at` past `created_at` — "they asked again". */
const askedAgain = (row: AccessRequestView): boolean => row.updated_at_iso !== row.created_at_iso;

export function AdminAccessRequestsTable(props: {
  readonly rows: readonly AccessRequestView[];
  readonly isBusy: (id: string) => boolean;
  readonly onInvite: (row: AccessRequestView) => void;
  readonly onDecline: (row: AccessRequestView) => void;
}): JSX.Element {
  const columns: readonly DataTableColumn<AccessRequestView>[] = [
    {
      key: 'applicant',
      rowHeader: true,
      header: t('admin.access_col_applicant'),
      cell: (row) => (
        <div class="admin-row__stack">
          <span>{row.name}</span>
          <span class="data-table__mono admin-row__meta">{row.email}</span>
          <Show when={row.org}>{(org) => <span class="admin-row__meta">{org()}</span>}</Show>
          <span class="admin-row__meta">{t('admin.access_source_waitlist')}</span>
        </div>
      ),
    },
    {
      key: 'note',
      header: t('admin.access_col_note'),
      // The note scrolls, so it is a named tab stop (`<section aria-label>`, the
      // `<ScrollPort/>` spelling) — a keyboard reader can scroll the rest.
      cell: (row) => (
        <section
          class="admin-row__text"
          tabindex="0"
          aria-label={t('admin.access_note_label', { name: row.name })}
        >
          {row.note ?? t('admin.access_no_note')}
        </section>
      ),
    },
    {
      key: 'status',
      header: t('admin.access_col_status'),
      cell: (row) => (
        <div class="admin-row__stack">
          <StatusPill tone={STATUS[row.status].tone}>{t(STATUS[row.status].key)}</StatusPill>
          <p class="admin-row__meta">
            {t('admin.access_applied_at', { at: absolute(row.created_at_iso) })}
          </p>
          <Show when={askedAgain(row)}>
            <p class="admin-row__meta">
              {t('admin.access_asked_again_at', { at: absolute(row.updated_at_iso) })}
            </p>
          </Show>
        </div>
      ),
    },
    {
      key: 'actions',
      header: t('admin.col_actions'),
      align: 'end',
      cell: (row) => (
        <Show
          when={row.status === 'pending'}
          fallback={
            <Show when={row.decided_at_iso}>
              {(at) => (
                <p class="admin-row__meta">
                  {t('admin.access_decided_at', { at: absolute(at()) })}
                </p>
              )}
            </Show>
          }
        >
          <div class="data-table__actions">
            <Button
              variant="outline"
              size="sm"
              disabled={props.isBusy(row.id)}
              onClick={() => props.onInvite(row)}
            >
              {t('admin.access_invite')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={props.isBusy(row.id)}
              onClick={() => props.onDecline(row)}
            >
              {t('admin.access_decline')}
            </Button>
          </div>
        </Show>
      ),
    },
  ];

  return (
    <DataTable
      label={t('admin.access_table_label')}
      columns={columns}
      rows={props.rows}
      rowKey={(row) => row.id}
    />
  );
}
